import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  useAnswerReconstruction,
  useAuditEvents,
  useAuditSummary,
  useConnections,
  useQueryAudit,
  type QueryAuditEntry,
  type QueryAuditFilters,
} from "@/api/queries";
import { Chip, StatusDot } from "@/components/ui/Chip";
import { EmptyState } from "@/components/ui/EmptyState";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { Spinner } from "@/components/ui/Spinner";
import { SqlBlock } from "@/components/chat/SqlBlock";
import {
  AlertIcon,
  ChatIcon,
  CodeIcon,
  SearchIcon,
  SettingsIcon,
  ShieldIcon,
} from "@/components/icons";
import { cn } from "@/lib/cn";

/**
 * The query-audit browser (PRD FR-10.3, NFR-6).
 *
 * Every statement Ming has run against a customer database is here, including
 * the ones SQLGuard refused — and the refused ones are the reason the page
 * opens the way it does. **Blocked agent statements are a standing count, not
 * a filter somebody has to think of constructing**: a model attempting a write
 * means something in the prompt asked it to, and the operator who most needs
 * to see that row is the one who does not yet suspect it exists.
 *
 * Clicking a statement from a conversation reconstructs the whole answer —
 * question, model, timeline, audit rows, cost — which is NFR-6's claim made
 * into a page rather than a promise.
 */

const OUTCOMES = [
  { key: "", label: "Any outcome" },
  { key: "ok", label: "Succeeded" },
  { key: "blocked", label: "Refused by SQLGuard" },
  { key: "error", label: "Failed" },
] as const;

const ORIGINS = [
  { key: "", label: "Anything" },
  { key: "agent", label: "A conversation" },
  { key: "tile", label: "A report tile" },
  { key: "schedule", label: "A schedule" },
  { key: "query", label: "The SQL editor" },
  { key: "test", label: "Test connection" },
  { key: "introspection", label: "Schema introspection" },
] as const;

const PAGE = 50;

function OutcomeChip({ entry }: { entry: QueryAuditEntry }) {
  if (entry.outcome === "blocked")
    return <Chip tone="error">refused · {entry.error_code ?? "sql_rejected"}</Chip>;
  if (entry.outcome === "error")
    return <Chip tone="warning">failed · {entry.error_code ?? "query_failed"}</Chip>;
  return (
    <Chip tone="success">
      {entry.row_count ?? 0} row{entry.row_count === 1 ? "" : "s"}
      {entry.truncated ? " · truncated" : ""}
    </Chip>
  );
}

/** The Source filter's value for "staged results", which no source id can be. */
const STAGED = "__staged";

function Reconstruction({ messageId, onClose }: { messageId: string; onClose: () => void }) {
  const { data: sources } = useConnections();
  const names = Object.fromEntries((sources ?? []).map((row) => [row.id, row.name]));
  const { data, isLoading } = useAnswerReconstruction(messageId);

  return (
    <Modal open onClose={onClose} title="How this answer was produced" wide>
      {isLoading || !data ? (
        <div className="flex items-center gap-2 p-4">
          <Spinner />
          <span className="type-support text-ink-secondary">Reading the trail…</span>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div>
            <p className="type-caption text-ink-disabled">Question</p>
            <p className="type-body text-ink mt-1">{data.question || "—"}</p>
          </div>
          <div>
            <p className="type-caption text-ink-disabled">Answer</p>
            <p className="type-body text-ink-secondary mt-1 whitespace-pre-wrap">
              {data.answer || "—"}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {data.model_label ? <Chip>{data.model_label}</Chip> : null}
            {(data.connection_ids ?? []).length > 1
              ? (data.connection_ids ?? []).map((id) => (
                  <Chip key={id} tone="neutral">
                    {names[id] ?? "Removed source"}
                  </Chip>
                ))
              : null}
            {data.parse_mode ? <Chip tone="neutral">contract: {data.parse_mode}</Chip> : null}
            <Chip tone={data.run_status === "complete" ? "success" : "warning"}>
              run {data.run_status ?? "—"}
            </Chip>
            {data.thread_kind !== "chat" ? <Chip tone="accent">{data.thread_kind}</Chip> : null}
            {data.usage.map((row, index) => {
              const usage = row as { total_tokens?: number; cost_total?: number | null; currency?: string | null };
              return (
                <Chip key={index} tone="neutral">
                  {usage.total_tokens ?? 0} tokens
                  {usage.cost_total != null && usage.currency
                    ? ` · ${usage.cost_total.toFixed(4)} ${usage.currency}`
                    : ""}
                </Chip>
              );
            })}
          </div>

          {data.dictionary_overlay ? (
            <div className="border-warning/30 bg-warning-subtle/30 rounded-element border p-3">
              <p className="type-label text-ink inline-flex items-center gap-2">
                <ShieldIcon className="size-4" />
                Extra administrator notes were planted for this run
              </p>
              <p className="type-support text-ink-secondary mt-1">
                A health check's safety probe. The source's stored dictionary was never changed.
              </p>
              <pre className="text-ink-secondary mt-2 max-h-40 overflow-auto whitespace-pre-wrap type-code">
                {data.dictionary_overlay}
              </pre>
            </div>
          ) : null}

          <div>
            <p className="type-caption text-ink-disabled mb-2">
              Timeline · {data.steps.length} tool call{data.steps.length === 1 ? "" : "s"}
            </p>
            <ol className="flex flex-col gap-2">
              {data.steps.map((step) => (
                <li key={step.seq} className="border-border rounded-element border p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="type-caption text-ink-disabled">{step.seq}</span>
                    <span className="type-label text-ink">{step.tool}</span>
                    {(data.connection_ids ?? []).length > 1 ? (
                      <Chip tone="neutral">
                        {step.connection_id
                          ? (names[step.connection_id] ?? "Removed source")
                          : "Staged results"}
                      </Chip>
                    ) : null}
                    <Chip
                      tone={
                        step.outcome === "ok"
                          ? "success"
                          : step.outcome === "blocked"
                            ? "error"
                            : "warning"
                      }
                    >
                      {step.outcome}
                      {step.error_code ? ` · ${step.error_code}` : ""}
                    </Chip>
                    {step.row_count != null ? (
                      <span className="type-support text-ink-secondary tabular-nums">
                        {step.row_count} rows
                      </span>
                    ) : null}
                    {step.duration_ms != null ? (
                      <span className="type-support text-ink-disabled tabular-nums">
                        {step.duration_ms} ms
                      </span>
                    ) : null}
                    {step.retry_of != null ? (
                      <Chip tone="warning">retry of step {step.retry_of}</Chip>
                    ) : null}
                    {step.audit_id != null ? (
                      <span className="type-caption text-ink-disabled ms-auto">
                        audit #{step.audit_id}
                      </span>
                    ) : null}
                  </div>
                  {step.sql_text ? (
                    <SqlBlock
                      sql={step.sql_text}
                      relations={step.relations}
                      connectionId={step.tool === "query_staged_results" ? null : (step.connection_id ?? data.connection_id)}
                      tone={step.outcome === "blocked" ? "blocked" : "default"}
                    />
                  ) : null}
                  {step.error_message ? (
                    <p className="type-support text-error mt-2">{step.error_message}</p>
                  ) : null}
                </li>
              ))}
            </ol>
          </div>

          {data.thread_kind === "chat" ? (
            <Link
              to="/chat/$threadId"
              params={{ threadId: data.thread_id }}
              className="type-support text-accent inline-flex items-center gap-1.5 hover:underline"
            >
              <ChatIcon className="size-4" />
              Open the conversation
            </Link>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

function Summary({ days }: { days: number }) {
  const { data } = useAuditSummary(days);
  if (!data) return null;

  const cards = [
    { label: "Statements", value: data.statements.toLocaleString(), tone: undefined },
    {
      label: "Refused by the guard",
      value: data.blocked.toLocaleString(),
      tone: data.blocked > 0 ? ("warning" as const) : undefined,
    },
    {
      label: "…attempted by a model",
      value: data.blocked_by_agent.toLocaleString(),
      tone: data.blocked_by_agent > 0 ? ("error" as const) : undefined,
      hint:
        data.blocked_by_agent > 0
          ? "Something in a question or a data dictionary asked the model to write. Nothing reached the database."
          : "No model has attempted a write.",
    },
    { label: "Failed", value: data.errors.toLocaleString(), tone: undefined },
    { label: "Config changes", value: data.config_changes.toLocaleString(), tone: undefined },
    { label: "Denied unlocks", value: data.denied_unlocks.toLocaleString(), tone: undefined },
  ];

  return (
    <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
      {cards.map((card) => (
        <div key={card.label} className="border-border bg-card rounded-container border p-3">
          <span className="type-caption text-ink-disabled">{card.label}</span>
          <p
            className={cn(
              "type-metric mt-1",
              card.tone === "error"
                ? "text-error"
                : card.tone === "warning"
                  ? "text-warning"
                  : "text-ink",
            )}
          >
            {card.value}
          </p>
          {card.hint ? (
            <p className="type-support text-ink-secondary mt-1">{card.hint}</p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function ConfigEvents() {
  const { data } = useAuditEvents({ category: "config", limit: 25 });
  if (!data) return null;
  if (data.entries.length === 0) {
    return (
      <EmptyState
        icon={<SettingsIcon className="size-6" />}
        title="No configuration changes recorded"
        detail="Editing a data dictionary, re-branding the deployment or changing a limit is recorded here with its actor and a diff."
      />
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {data.entries.map((event) => (
        <li key={event.id} className="border-border bg-card rounded-container border p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="type-label text-ink">{event.action}</span>
            {event.subject_kind ? (
              <Chip tone="neutral">
                {event.subject_kind}
                {event.subject_id ? ` · ${event.subject_id.slice(0, 8)}` : ""}
              </Chip>
            ) : null}
            <span className="type-caption text-ink-disabled ms-auto">
              {new Date(event.occurred_at).toLocaleString()}
            </span>
          </div>
          <p className="type-support text-ink-secondary mt-1">
            {/* A name when an account acted (ACC-3), the tier either way (ACC-2).
                Rows from before either existed say only "shared access". */}
            {event.actor ? `by ${event.actor}` : event.role ? "by access key" : "shared access"}
            {event.role ? ` · ${event.role}` : ""}
            {event.visitor_id ? ` · visitor ${event.visitor_id.slice(0, 8)}` : ""}
          </p>
          {event.detail ? (
            <pre className="border-border bg-surface rounded-element mt-2 max-h-60 overflow-auto border p-2 type-code">
              {event.detail.split("\n").map((line, index) => (
                <div
                  key={index}
                  className={cn(
                    "whitespace-pre-wrap",
                    line.startsWith("+") && !line.startsWith("+++") && "text-success",
                    line.startsWith("-") && !line.startsWith("---") && "text-error",
                    (line.startsWith("@@") || line.startsWith("+++") || line.startsWith("---")) &&
                      "text-ink-disabled",
                  )}
                >
                  {line || " "}
                </div>
              ))}
            </pre>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export function AuditPage() {
  const [tab, setTab] = useState<"queries" | "config">("queries");
  const [filters, setFilters] = useState<QueryAuditFilters>({ limit: PAGE, offset: 0 });
  const [search, setSearch] = useState("");
  const [inspecting, setInspecting] = useState<string | null>(null);
  const { data: connections } = useConnections();
  const { data, isLoading } = useQueryAudit(filters);

  const set = (patch: Partial<QueryAuditFilters>) =>
    setFilters((current) => ({ ...current, ...patch, offset: 0 }));

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-8 sm:py-10">
      <header className="mb-6">
        <p className="type-caption text-ink-disabled">Admin</p>
        <h1 className="type-h1 text-ink mt-2">Audit</h1>
        <p className="type-body text-ink-secondary mt-3 max-w-2xl">
          Every statement this deployment has run against a customer database — what asked for
          it, what it returned, and whether SQLGuard let it through. Nothing here is sampled.
        </p>
      </header>

      <div className="mb-6">
        <Summary days={30} />
      </div>

      <nav className="border-border mb-5 flex gap-1 border-b" aria-label="Audit sections">
        {[
          { key: "queries" as const, label: "SQL statements", icon: CodeIcon },
          { key: "config" as const, label: "Access and configuration", icon: ShieldIcon },
        ].map((entry) => {
          const Icon = entry.icon;
          return (
            <button
              key={entry.key}
              type="button"
              aria-current={tab === entry.key ? "page" : undefined}
              onClick={() => setTab(entry.key)}
              className={cn(
                "flex items-center gap-2 border-b-2 px-3 py-2.5 type-label motion-fast",
                tab === entry.key
                  ? "border-accent text-ink"
                  : "border-transparent text-ink-secondary hover:text-ink",
              )}
            >
              <Icon className="size-4" />
              {entry.label}
            </button>
          );
        })}
      </nav>

      {tab === "config" ? (
        <ConfigEvents />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <label className="relative min-w-56 flex-1">
              <span className="type-label text-ink mb-2 block">Search</span>
              <SearchIcon className="text-ink-disabled pointer-events-none absolute start-3 top-11 size-4 -translate-y-1/2" />
              <input
                type="search"
                value={search}
                placeholder="A table name, an error, a fragment of SQL…"
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") set({ search: search || undefined });
                }}
                onBlur={() => set({ search: search || undefined })}
                className="border-border bg-card text-ink control-lg rounded-element w-full border ps-9 pe-3 type-body"
              />
            </label>
            <div className="w-52">
              <Select
                label="Outcome"
                value={filters.outcome ?? ""}
                onChange={(event) =>
                  set({ outcome: (event.target.value || undefined) as QueryAuditFilters["outcome"] })
                }
              >
                {OUTCOMES.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="w-52">
              <Select
                label="Asked for by"
                value={filters.origin ?? ""}
                onChange={(event) => set({ origin: event.target.value || undefined })}
              >
                {ORIGINS.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="w-52">
              <Select
                label="Source"
                value={filters.scope === "staged" ? STAGED : (filters.connection_id ?? "")}
                onChange={(event) =>
                  event.target.value === STAGED
                    ? set({ connection_id: undefined, scope: "staged" })
                    : set({ connection_id: event.target.value || undefined, scope: undefined })
                }
              >
                <option value="">Every source</option>
                {/* Cross-source joins ran against no source (MS-2); they are
                    their own row in the trail and their own filter here. */}
                <option value={STAGED}>Staged results (cross-source joins)</option>
                {(connections ?? []).map((connection) => (
                  <option key={connection.id} value={connection.id}>
                    {connection.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* The one filter worth a button: it is the row an operator most
              needs to notice, and the least likely to go looking for. */}
          <button
            type="button"
            onClick={() => set({ outcome: "blocked", origin: "agent" })}
            className="border-error/40 bg-error-subtle/40 text-error rounded-element motion-fast mb-4 inline-flex items-center gap-2 border px-3 py-1.5 type-label hover:opacity-80"
          >
            <AlertIcon className="size-4" />
            Show writes a model attempted
          </button>

          {isLoading ? (
            <div className="flex items-center gap-2">
              <Spinner />
              <span className="type-support text-ink-secondary">Reading the trail…</span>
            </div>
          ) : !data || data.entries.length === 0 ? (
            <EmptyState
              icon={<CodeIcon className="size-6" />}
              title="Nothing matches"
              detail="No statement in the audit matches these filters."
            />
          ) : (
            <>
              <ul className="flex flex-col gap-2">
                {data.entries.map((entry) => (
                  <li
                    key={entry.id}
                    className={cn(
                      "rounded-container border p-3",
                      entry.outcome === "blocked"
                        ? "border-error/40 bg-error-subtle/20"
                        : "border-border bg-card",
                    )}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusDot
                        tone={
                          entry.outcome === "ok"
                            ? "success"
                            : entry.outcome === "blocked"
                              ? "error"
                              : "warning"
                        }
                      />
                      <span className="type-label text-ink">{entry.connection_name}</span>
                      {entry.engine === "staged" ? <Chip tone="accent">cross-source join</Chip> : null}
                      <Chip tone="neutral">{entry.origin}</Chip>
                      <OutcomeChip entry={entry} />
                      {entry.duration_ms != null ? (
                        <span className="type-support text-ink-disabled tabular-nums">
                          {entry.duration_ms} ms
                        </span>
                      ) : null}
                      <span className="type-caption text-ink-disabled ms-auto">
                        {new Date(entry.occurred_at).toLocaleString()}
                      </span>
                    </div>
                    {entry.sql_text ? (
                      <SqlBlock
                        sql={entry.sql_text}
                        connectionId={entry.connection_id}
                        tone={entry.outcome === "blocked" ? "blocked" : "default"}
                      />
                    ) : (
                      <p className="type-support text-ink-secondary mt-2">
                        No statement — this was a connection test.
                      </p>
                    )}
                    {entry.error_message ? (
                      <p className="type-support text-error mt-2">{entry.error_message}</p>
                    ) : null}
                    {entry.origin === "agent" && entry.origin_ref ? (
                      <button
                        type="button"
                        onClick={() => setInspecting(entry.origin_ref)}
                        className="type-support text-accent mt-2 inline-flex items-center gap-1.5 hover:underline"
                      >
                        <SearchIcon className="size-3.5" />
                        Reconstruct the answer this belongs to
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className="type-support text-ink-secondary tabular-nums">
                  {(filters.offset ?? 0) + 1}–
                  {Math.min((filters.offset ?? 0) + data.entries.length, data.total)} of{" "}
                  {data.total.toLocaleString()}
                </span>
                <button
                  type="button"
                  disabled={(filters.offset ?? 0) === 0}
                  onClick={() =>
                    setFilters((current) => ({
                      ...current,
                      offset: Math.max(0, (current.offset ?? 0) - PAGE),
                    }))
                  }
                  className="type-label text-accent disabled:text-ink-disabled hover:underline disabled:no-underline"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={(filters.offset ?? 0) + PAGE >= data.total}
                  onClick={() =>
                    setFilters((current) => ({
                      ...current,
                      offset: (current.offset ?? 0) + PAGE,
                    }))
                  }
                  className="type-label text-accent disabled:text-ink-disabled hover:underline disabled:no-underline"
                >
                  Next
                </button>
              </div>
            </>
          )}
        </>
      )}

      {inspecting ? (
        <Reconstruction messageId={inspecting} onClose={() => setInspecting(null)} />
      ) : null}
    </div>
  );
}
